import React, { useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { loginThenComeBack, navigate } from '@gumtree/shell/src/common/common-actions';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import { Icon } from '@gumtree/ui-library';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import type { L1Category } from '@gumtree/shared/src/types/client-data';
import { setRequestId } from '@gumtree/shell/src/request-id';
import { isOutOfCoreHours } from '@gumtree/shared/src/util/core-hours';
import { useFeatureValue } from '@growthbook/growthbook-react';
import type { SendMessageEventSourceType } from '@gumtree/shared/src/types/send-message';
import type revealPhoneUtil from '../seller-contact-block/reveal-phone-util';
import type { VipState, VipUseDispatch } from '../../reducers';
import { StyledButton, InlineButton } from './style';
import MakeOffer from '../seller-contact-block/make-offer/make-offer';
import { RequestAQuoteFormContext, MotorsCTAContext } from '../../vip-context';
import { updatePostCallReminder, updateShowMessageEncourage, updateShowOutOfHoursMessage } from '../../reducers/post-call';

export const stickyContactEmailDQ = 'sticky-contact-email';
export const fixedContactEmailDQ = 'contact-email';
const ContactButton = ({
    gridColumn,
    display = 'primary',
    ...props
}: React.ComponentProps<typeof StyledButton>) => (
    <StyledButton gridColumn={gridColumn} display={display} fullWidth {...props} />
);

const ContactButtons: React.FC<Props> = ({
    replyEmail,
    replyLink,
    replyPhone,
    replySmsEnabled,
    revealPhoneUtil,
    isStickyHeader = false,
    isMobile,
    l1CategorySeoName,
    skipLoginToReveal,
    onlyInlineMessage,
    messageEventSource,
    isPurchaseAvailable,
    onClickBegin,
}) => {
    const { sellerContactDetails, adDetails, baseConfig, userData, requestId } = useSelector(
        selectState,
        shallowEqual
    );

    const { adSource, openRequestAQuoteForm } = useContext(RequestAQuoteFormContext);
    const { isMotorsCTATask1, isMotorsCTATask2, isMotorsCTATask3_B, isMotorsCTATask3_C } = useContext(MotorsCTAContext);

    const { openDialog } = useModal();
    const userLoggedIn = !!userData && userData.userLoggedIn;
    const makeAnOfferEnabled = useSelector(
        ({ sellerActions }: VipState) => sellerActions.makeAnOfferEnabled
    );

    const { id, isCurrentUserSeller, isFromMotorsFeed } = adDetails;
    const stickyPrefix = isStickyHeader ? 'sticky-' : '';
    const isJobs = l1CategorySeoName === 'jobs';
    const isServices = l1CategorySeoName === 'business-services';
    const isMotors = l1CategorySeoName === 'cars-vans-motorbikes';
    const isForSale = l1CategorySeoName === 'for-sale';
    const isProperty = l1CategorySeoName === 'flats-houses';

    const dispatch = useDispatch() as VipUseDispatch;
    const shouldShowApplyOnSiteButton = replyLink && isJobs;
    const shouldShowEmailButton = adSource === 'Bark' || (!!replyEmail && (!isMotors || isMobile));

    const genReplyMessageUrl = (outOfHours = false) => {
        const queryParams = new URLSearchParams();
        if (isCurrentUserSeller) queryParams.set('ius', 'true');
        if (messageEventSource) queryParams.set('eventsource', messageEventSource);
        if (outOfHours) queryParams.set('outofhours', 'true');
        return `/reply-message/${id}?${queryParams.toString()}`;
    };

    const contactByEmail = async () => {
        trackPermutiveCustomEvent(window, PermutiveEventNames.R2S_EMAIL_BEGIN, {}, true);
        let listingDetails = {};
        try {
            if (window.gumtreeDataLayer && Array.isArray(window.gumtreeDataLayer)) {
                const pageDeclarationEvent = window.gumtreeDataLayer.find(item => item.event === 'pageDeclaration');
                if (pageDeclarationEvent && pageDeclarationEvent.listingDetails) {
                    listingDetails = pageDeclarationEvent.listingDetails;
                }
            }
        } catch (error) {
            console.error('Error getting listing details', error);
        }
        trackGA4PreNav<GA4.ContactSellerClickEvent>({
            event: 'contact_a_seller',
            contactASellerMethod: 'click email link',
            formValidation: 'success',
            listingPartner: isFromMotorsFeed ? 'motors' : undefined,
            listingDetails,
            entryPoint: 'vip',
        });
        setRequestId(`/reply-confirmation/${id}`);
        const redirectUrl = isFromMotorsFeed
            ? genReplyMessageUrl()
            : `/reply/${id}${isCurrentUserSeller ? `?ius=true` : ``}`;
        dispatch(navigate(redirectUrl));
    };

    const isUI_202506 = useFeatureValue(GrowthBookFeature.SERVICE_NEW_UI, 'A') === 'B' && isServices;

    const getLabel = () => {
        let label = 'Email';
        if (isJobs) {
            label = 'Apply';
        } else if (isServices) {
            label = 'Request a quote';
        } else if ( isForSale || isMotors || isProperty) {
            label = 'Message';
        }
        return label;
    };
        
    const handleContactByEmail = (type: 'email' | 'inlineMessage') => async (e) => {
        onClickBegin?.(type);

        trackGA4Event<any>({
            event: 'ad_click_button_info',
            requestId,
            listingId: id
        });

        if (adSource === 'Bark') {
            if (!userLoggedIn) {
                openDialog(e, LoginModalType.REQUEST_QUOTE, true);
            } else {
                openRequestAQuoteForm('quote');
            }
            return;
        }

        if (!userLoggedIn) {
            const redirectUrl = isFromMotorsFeed ? genReplyMessageUrl() : undefined;
            openDialog(e, LoginModalType.EMAIL, true, redirectUrl);
            return;
        }

        if (isMotors && !userLoggedIn) {
            dispatch(loginThenComeBack());
            return;
        }

        await contactByEmail();
    };
        
    const revealPhone = (type: 'tel' | 'sms') => (e) => {
        onClickBegin?.(type);

        trackGA4Event<any>({
            event: 'ad_click_button_info',
            requestId,
            listingId: id
        });

        if (adSource === 'Bark') {
            if (!userLoggedIn) {
                openDialog(e, LoginModalType.REQUEST_QUOTE, true);
            } else {
                const preferredContactType = type === 'tel' ? 'call' : 'quote';
                openRequestAQuoteForm(preferredContactType);
            }
            return;
        }

        if (!skipLoginToReveal && !userLoggedIn) {
            const url = new URL(location.href);
            url.searchParams.set('revealPhone', 'true');
            history.replaceState({}, '', url);
            openDialog(e, LoginModalType.REVEAL_PHONE, true);
        } else {
            if (isMotorsCTATask3_B && isOutOfCoreHours()) {
                dispatch(navigate(genReplyMessageUrl(true)));
                return;
            }

            if (isMotorsCTATask3_C && isOutOfCoreHours()) {
                dispatch(updateShowOutOfHoursMessage(true));
                return;
            }

            revealPhoneUtil(e, dispatch, sellerContactDetails, adDetails, baseConfig, type, skipLoginToReveal, (type: 'reveal' | 'redirectToApp') => {
                if (type !== 'redirectToApp' || !isMobile) return;
                isMotorsCTATask1 && dispatch(updatePostCallReminder(true));
                isMotorsCTATask2 && userLoggedIn && dispatch(updateShowMessageEncourage(true));
            });
        }
    };

    React.useEffect(() => {
        const url = new URL(location.href);
        if (userLoggedIn && url.searchParams.has('revealPhone')) {
            url.searchParams.delete('revealPhone');
            history.replaceState({}, '', url);
            revealPhoneUtil(null, dispatch, sellerContactDetails, adDetails, baseConfig);
        }
    }, []);

    if (onlyInlineMessage) {
        return (
            <InlineButton
                disabled={isCurrentUserSeller}
                onClick={handleContactByEmail('inlineMessage')}
                label="Send a message"
            />
        );
    }

    return (
        <>
            {(shouldShowEmailButton || adSource === 'Bark') && (
                <ContactButton
                    gridColumn="reply-email"
                    noFollow
                    disabled={isCurrentUserSeller}
                    onClick={handleContactByEmail('email')}
                    label={getLabel()}
                    isMotorsCTATask1={isMotorsCTATask1}
                    icon={<Icon type={(isUI_202506 || isMotorsCTATask1) ? 'sendMsg' : "envelope"} />}
                    data-q={stickyPrefix ? stickyContactEmailDQ : fixedContactEmailDQ}
                    display={isPurchaseAvailable ? 'secondary' : 'primary'}
                />
            )}
            {(shouldShowApplyOnSiteButton && adSource !== 'Bark') && (
                <ContactButton
                    gridColumn="reply-email"
                    noFollow
                    disabled={Boolean(isCurrentUserSeller)}
                    href={replyLink}
                    onClick={(e) => {
                        e.preventDefault();
                        
                        if (adSource === 'Bark') {
                            openRequestAQuoteForm('quote');
                            return;
                        }

                        onClickBegin?.('link');

                        trackGA4Event<GA4.ContactSellerExternalEvent>({
                            event: 'contact_seller_external',
                            linkURL: replyLink,
                            linkDomain: getDomainFromUrl(replyLink),
                            linkText: 'Apply on company site',
                            formValidation: 'success',
                        });

                        window.open(replyLink, '_blank');
                    }}
                    label="Apply on company site"
                    icon={<Icon type="envelope" />}
                    display="primary"
                />
            )}
            {isMobile && replyPhone && (!isStickyHeader || !isPurchaseAvailable) && (
                <>
                    <ContactButton
                        gridColumn="reply-phone"
                        noFollow
                        disabled={Boolean(isCurrentUserSeller)}
                        label={adSource === 'Bark' ? 'Call back' : isJobs ? 'Call to apply' : 'Call'}
                        isMotorsCTATask1={isMotorsCTATask1}
                        icon={<Icon type={ (isUI_202506 || isMotorsCTATask1) ? 'phoneCall' : 'phone' } />}
                        data-q={`${stickyPrefix}reply-phone`}
                        onClick={revealPhone('tel')}
                        display={(isMotorsCTATask1 || (isUI_202506 && !(isStickyHeader && replySmsEnabled))) ? 'secondary' : 'primary'} // 新样式的sticky bar中，有sms时电话用绿色按钮
                    />
                    {replySmsEnabled && (
                        <ContactButton
                            gridColumn="reply-sms"
                            label="SMS"
                            disabled={Boolean(isCurrentUserSeller)}
                            data-q={`${stickyPrefix}reply-sms`}
                            onClick={revealPhone('sms')}
                            display="primary"
                        />
                    )}
                    {!!replyEmail && makeAnOfferEnabled && !isStickyHeader && adSource !== 'Bark' && <MakeOffer />}
                </>
            )}
        </>
    );
};

export type ContactType = 'email' | 'link' | 'tel' | 'sms' | 'inlineMessage';

export interface Props {
    replyEmail?: string;
    replyLink?: string;
    replyPhone?: string;
    replySmsEnabled: boolean;
    revealPhoneUtil: typeof revealPhoneUtil;
    isStickyHeader?: boolean;
    l1CategorySeoName: L1Category | undefined;
    isMobile: boolean;
    skipLoginToReveal?: boolean;
    onlyInlineMessage?: boolean;
    messageEventSource?: SendMessageEventSourceType;
    isPurchaseAvailable?: boolean;
    onClickBegin?: (type: ContactType) => void;
}

function selectState({ sellerContactDetails, adDetails, baseConfig, userData, requestId, postCall }: VipState) {
    return {
        sellerContactDetails,
        adDetails,
        baseConfig,
        userData,
        requestId,
        postCall,
    };
}

export default ContactButtons;
